import { MoonfallErrorRingBuffer } from './error-ring-buffer.js';

let MoonfallClickGuard;

/**
 * Shared client-side click-guard: ensures a button can only fire one async
 * action at a time, swaps to a "pending" visual + aria-busy, and can hold a
 * cooldown past resolution so a server-side throttle window is mirrored on
 * the UI. Mirrors the IIFE pattern of error-ring-buffer.js so it can load
 * before convex-client.js without dependencies.
 */
(function () {
  if (typeof window === 'undefined') return;
  if (MoonfallClickGuard) return;

  var BTN_PENDING_CLASS = 'btn-pending';

  function recordError(message) {
    try {
      if (MoonfallErrorRingBuffer && typeof MoonfallErrorRingBuffer.push === 'function') {
        MoonfallErrorRingBuffer.push({ t: Date.now(), kind: 'guard', msg: String(message) });
      }
    } catch (_) {
      // never propagate ring-buffer errors
    }
  }

  function nextGen(button) {
    var current = Number(button.dataset.guardGen) || 0;
    var next = current + 1;
    button.dataset.guardGen = String(next);
    return next;
  }

  function setPending(button, label) {
    button.dataset.guardPending = '1';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add(BTN_PENDING_CLASS);
    if (typeof label === 'string' && label.length > 0) {
      if (button.dataset.guardOriginalText == null) {
        button.dataset.guardOriginalText = button.textContent;
      }
      button.textContent = label;
    }
  }

  function clearPending(button, generation) {
    if (generation != null && Number(button.dataset.guardGen) !== generation) return;
    delete button.dataset.guardPending;
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.classList.remove(BTN_PENDING_CLASS);
    if (button.dataset.guardOriginalText != null) {
      button.textContent = button.dataset.guardOriginalText;
      delete button.dataset.guardOriginalText;
    }
    if (button.dataset.guardCooldownTimer) {
      try { clearTimeout(Number(button.dataset.guardCooldownTimer)); } catch (_) {}
      delete button.dataset.guardCooldownTimer;
    }
  }

  function guard(button, asyncFn, opts) {
    if (!button || typeof button.setAttribute !== 'function') {
      return Promise.reject(new Error('MoonfallClickGuard.guard: button is required'));
    }
    if (typeof asyncFn !== 'function') {
      return Promise.reject(new Error('MoonfallClickGuard.guard: asyncFn must be a function'));
    }
    if (button.dataset.guardPending === '1') {
      return Promise.resolve({ suppressed: true });
    }

    var options = opts || {};
    var cooldownMs = Number(options.cooldownMs) || 0;
    setPending(button, options.label);
    var myGen = nextGen(button);

    var scheduleRelease = function () {
      if (cooldownMs > 0) {
        var timerId = setTimeout(function () { clearPending(button, myGen); }, cooldownMs);
        button.dataset.guardCooldownTimer = String(timerId);
      } else {
        clearPending(button, myGen);
      }
    };

    return new Promise(function (resolve, reject) {
      var result;
      try {
        result = asyncFn();
      } catch (err) {
        clearPending(button, myGen);
        recordError(err && err.message ? err.message : err);
        reject(err);
        return;
      }
      Promise.resolve(result).then(function (value) {
        scheduleRelease();
        resolve(value);
      }, function (err) {
        clearPending(button, myGen);
        recordError(err && err.message ? err.message : err);
        reject(err);
      });
    });
  }

  function wrap(button, handler, opts) {
    return function (event) {
      guard(button, function () { return handler(event); }, opts).catch(function () {
        // errors already recorded via the ring buffer
      });
    };
  }

  function isPending(button) {
    return !!(button && button.dataset && button.dataset.guardPending === '1');
  }

  function forceRelease(button) {
    if (!button || !button.dataset) return;
    nextGen(button);
    clearPending(button, null);
  }

  MoonfallClickGuard = {
    guard: guard,
    wrap: wrap,
    isPending: isPending,
    forceRelease: forceRelease,
  };
})();

export { MoonfallClickGuard };
